import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Users, MapPin, Mail, Phone, UserPlus, ArrowLeft, Filter, X } from 'lucide-react';
import { teamAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface AvailableMember {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  skills?: {
    technical?: string[];
    soft?: string[];
  };
  primarySkills?: string[];
  secondarySkills?: string[];
  experience?: Array<{
    company: string;
    position: string;
    startDate: string;
    endDate?: string;
    isCurrent: boolean;
    description?: string;
  }>;
  education?: {
    degree?: string;
    institution?: string;
    fieldOfStudy?: string;
    graduationYear?: number;
  };
  professionalInfo?: {
    currentCompany?: string;
    currentPosition?: string;
    yearsOfExperience?: number;
    industry?: string;
  };
}

interface Team {
  _id: string;
  name: string;
  hackathon: {
    _id: string;
    title: string;
    maxTeamSize: number;
  };
  members: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
  }>;
}

const AddMembers: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuth();

  const [team, setTeam] = useState<Team | null>(null);
  const [availableMembers, setAvailableMembers] = useState<AvailableMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMember, setSelectedMember] = useState<AvailableMember | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [allMembers, setAllMembers] = useState<AvailableMember[]>([]);

  // Fetch team details and available members
  useEffect(() => {
    if (teamId) {
      fetchTeamAndMembers();
    }
  }, [teamId]);

  const fetchTeamAndMembers = async () => {
    try {
      setLoading(true);
      
      // Fetch team details
      const teamData = await teamAPI.getTeamById(teamId!);
      setTeam(teamData);
      
      // Fetch available members
      await fetchAvailableMembers(1);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch team details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMembers = async (page: number, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Fetch all members in one request for client-side search
      const response = await teamAPI.getAvailableMembers(teamId!, 1, 1000);
      
      setAllMembers(response.users);
      setAvailableMembers(response.users);
      setTotalMembers(response.total);
      setCurrentPage(1);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch available members');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Utility functions (moved before useMemo to avoid hoisting issues)
  const getCityFromAddress = (address: string) => {
    if (!address) return 'Location not specified';
    
    const addressParts = address.split(',').map(part => part.trim());
    
    for (let i = addressParts.length - 1; i >= 0; i--) {
      const part = addressParts[i];
      if (!/^\d{6}$/.test(part) && 
          !['India', 'Telangana', 'Andhra Pradesh', 'Karnataka', 'Maharashtra'].includes(part)) {
        return part;
      }
    }
    
    return 'Location not specified';
  };

  const getUserSkills = (member: AvailableMember) => {
    const skills = [];
    if (member.skills?.technical) skills.push(...member.skills.technical);
    if (member.skills?.soft) skills.push(...member.skills.soft);
    if (member.primarySkills) skills.push(...member.primarySkills);
    if (member.secondarySkills) skills.push(...member.secondarySkills);
    return [...new Set(skills)];
  };

  const getMemberExperience = (member: AvailableMember) => {
    // Handle experience array (new format)
    if (member.experience && Array.isArray(member.experience) && member.experience.length > 0) {
      const totalYears = member.experience.reduce((total, exp) => {
        const startDate = new Date(exp.startDate);
        const endDate = exp.endDate ? new Date(exp.endDate) : new Date();
        const years = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
        return total + years;
      }, 0);
      return `${Math.round(totalYears)} years`;
    }
    
    // Handle professionalInfo (legacy format)
    if (member.professionalInfo?.yearsOfExperience) {
      return `${member.professionalInfo.yearsOfExperience} years`;
    }
    
    // Handle education-based calculation
    if (member.education?.graduationYear) {
      const currentYear = new Date().getFullYear();
      const yearsSinceGraduation = currentYear - member.education.graduationYear;
      if (yearsSinceGraduation >= 0) {
        return yearsSinceGraduation === 0 ? 'Student' : `${yearsSinceGraduation} years`;
      }
    }
    
    return 'Student';
  };

  // Client-side search filtering
  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) {
      return allMembers;
    }

    const searchLower = searchTerm.toLowerCase();
    return allMembers.filter(member => {
      const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
      const email = member.email.toLowerCase();
      const phone = member.phone?.toLowerCase() || '';
      const location = getCityFromAddress(member.address || '').toLowerCase();
      const skills = getUserSkills(member).join(' ').toLowerCase();
      const experience = getMemberExperience(member).toLowerCase();

      return (
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchLower) ||
        location.includes(searchLower) ||
        skills.includes(searchLower) ||
        experience.includes(searchLower)
      );
    });
  }, [allMembers, searchTerm]);

  const handleAddMember = (member: AvailableMember) => {
    setSelectedMember(member);
    setShowConfirmation(true);
  };

  const confirmAddMember = async () => {
    if (!selectedMember) return;

    try {
      setAddingMember(true);
      await teamAPI.addMemberToTeam(teamId!, selectedMember._id);
      
      success('Member added successfully!');
      setShowConfirmation(false);
      setSelectedMember(null);
      
      // Refresh team and members
      await fetchTeamAndMembers();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  // Load more functionality removed for client-side search

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center">
          <p className="text-gray-600">Team not found</p>
        </div>
      </div>
    );
  }

  const remainingSlots = team.hackathon.maxTeamSize - team.members.length;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add Team Members</h1>
            <p className="text-gray-600 mt-1">
              Browse and add available members to {team.name}
            </p>
          </div>
        </div>

        {/* Team Info */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{team.name}</h2>
                <p className="text-gray-600">{team.hackathon.title}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Team Members</p>
                <p className="text-lg font-semibold text-gray-900">
                  {team.members.length}/{team.hackathon.maxTeamSize}
                </p>
                {remainingSlots > 0 && (
                  <p className="text-sm text-green-600">{remainingSlots} slot(s) available</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, phone, location, skills, or experience..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Available Members ({filteredMembers.length})
            </h3>
            {remainingSlots <= 0 && (
              <Badge className="bg-red-100 text-red-800">
                Team at maximum capacity
              </Badge>
            )}
          </div>

          {filteredMembers.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {searchTerm ? 'No members found matching your search.' : 'No available members found.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMembers.map((member) => (
                <Card key={member._id} className="hover:shadow-lg transition-shadow flex flex-col h-full">
                  <CardContent className="pt-6 flex flex-col flex-1">
                    {/* Member Basic Info */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">
                        {member.firstName} {member.lastName}
                      </h4>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <span>{member.email}</span>
                        </div>
                        {member.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <span>{member.phone}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>{getCityFromAddress(member.address || '')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Experience */}
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Experience</p>
                      <Badge variant="outline" className="text-xs">
                        {getMemberExperience(member)}
                      </Badge>
                    </div>

                    {/* Skills */}
                    {getUserSkills(member).length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Skills</p>
                        <div className="flex flex-wrap gap-1">
                          {getUserSkills(member).slice(0, 5).map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                          {getUserSkills(member).length > 5 && (
                            <Badge variant="outline" className="text-xs">
                              +{getUserSkills(member).length - 5} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Add Button */}
                    <div className="mt-auto pt-4">
                      <Button
                        onClick={() => handleAddMember(member)}
                        disabled={remainingSlots <= 0}
                        className="w-full"
                        size="sm"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add to Team
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Load More functionality removed for client-side search */}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member to Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to add this member to your team?
            </DialogDescription>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-lg">
                  {selectedMember.firstName} {selectedMember.lastName}
                </h4>
                <p className="text-gray-600">{selectedMember.email}</p>
                <p className="text-gray-600">{getCityFromAddress(selectedMember.address || '')}</p>
                <p className="text-gray-600">Experience: {getMemberExperience(selectedMember)}</p>
              </div>
              
              <div className="text-sm text-gray-600">
                <p>This member will receive an email notification and be added to your team immediately.</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmation(false)}
              disabled={addingMember}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAddMember}
              disabled={addingMember}
            >
              {addingMember ? <LoadingSpinner size="sm" /> : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddMembers; 